import React from 'react';

import { twMerge } from 'tailwind-merge';
import { getCountryUrl, getRegionUrl } from '../../helpers/urls';
import { InternalLink } from '../Clickables/InternalLink/InternalLink';
import { Text } from '../Text/Text';
import { variants } from '../Text/variants';

interface IProps {
  regionId?: string | null;
  regionName?: string | null;
  countryId?: string;
  countryName?: string;
  countyName?: string;
  className?: string;
  LinkClassName?: string;
  textAs?: string | React.ComponentType<any> | React.FunctionComponent<any>;
  textVariant?: keyof typeof variants;
}

/**
 * `LocationLinks` component renders links to a region and country based on provided IDs and names.
 *
 * @param {Object} props - The properties object.
 * @param {string | null} [props.regionId] - The ID of the region (optional).
 * @param {string | null} [props.regionName] - The name of the region (optional).
 * @param {string} props.countryId - The ID of the country.
 * @param {string} props.countryName - The name of the country.
 * @param {string} [props.className] - Additional class names for the `Text` component (optional).
 * @param {string} [props.LinkClassName] - Additional class names for the `InternalLink` component (optional).
 * @param {string | React.ComponentType<any> | React.FunctionComponent<any>} [props.textAs='p'] - The HTML tag or component to render the text as (optional).
 * @param {keyof typeof variants} [props.textVariant='xs'] - The variant of the text (optional).
 *
 * @returns {JSX.Element} The rendered `LocationLinks` component.
 */
export const LocationLinks = ({
  regionId,
  regionName,
  countryId,
  countryName,
  countyName,
  className,
  LinkClassName,
  textAs = 'p',
  textVariant = 'xs',
}: IProps) => {
  return (
    <>
      {countyName && <>{countyName}, </>}
      {countryId && (
        <Text as={textAs} variant={textVariant} className={twMerge('m-0 text-center', className)}>
          {countryId && regionId && (
            <>
              <InternalLink
                className={LinkClassName}
                to={getRegionUrl(countryId, regionId, countryName ?? '', regionName ?? '')}
              >
                {regionName}
              </InternalLink>
              <span>{', '}</span>
            </>
          )}
          {countryId && (
            <InternalLink
              className={LinkClassName}
              to={getCountryUrl(countryId, countryName ?? '')}
            >
              {countryName ?? ''}
            </InternalLink>
          )}
        </Text>
      )}
    </>
  );
};
